import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import CreditCardScreen from 'components/Payments/CreditCardScreen';
import ProgressMessage from 'components/Utils/ProgressMessage/index';
import './style.scss';
import DefaultButton from 'components/Utils/NewDefaultButton';
import CreditCardImage from 'components/Payments/CreditCardImage';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import { HST } from 'components/Payments/ChargePatientForm';
import SubscriptionUtils from 'services/SubscriptionUtils';
import PatientAddCardModal from 'components/Payments/CreditCardScreen/PatientAddCardModal';
import PromoCodeModal from 'components/Payments/CreditCardScreen/PromoCodeModal';
import AnalyticsContext from 'contexts/AnalyticsContext';
import { useAsyncMemo } from 'services/TypeUtils';

const SCREENS = {
  creditCard: 'creditCard',
  loading: 'loading',
};

const Subscription = ({ patient, onSuccess, goToStart }) => {
  const dispatch = useAppDispatch();
  const { trackTagManager } = useContext(AnalyticsContext);

  const subscriptionFlow = useAppSelector((state) => state.payments.subscriptionFlow);
  const planId = subscriptionFlow.plan?.id;
  const signature = subscriptionFlow.signature;
  const creditCards = useAppSelector((state) => state.payments.creditCards);
  const plans = useAppSelector((state) => state.payments.plans);

  const plan = plans.find((p) => p.id === planId);

  const isSubscribing = useIsLoading(api.Payments.createSubscription);

  const [loading, setLoading] = useState(false);
  const [promoCodeModal, setPromoCodeModal] = useState(false);
  const [creditCardModal, setCreditCardModal] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(SCREENS.creditCard);

  useEffect(() => {
    if (!plan) goToStart();
  }, [plan]);

  const patientId = patient.id;

  useEffect(() => {
    dispatch(api.Payments.getCreditCards({ patientId }));
  }, [dispatch, patientId]);

  useEffect(() => {
    setLoading(true);
    if (plan && !plan.amount) {
      subscribeWrapper()
        .then(() => {
          onSuccess();
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [plan]);

  async function subscribeWrapper() {
    let paymentPlanId = plan.id;

    let result = await dispatch(
      api.Payments.createSubscription({
        paymentPlanId,
        patientId,
        stringResource: signature ? { value: signature } : undefined,
      }),
      { errorTitle: 'Subscription' },
    );

    if (result) {
      trackTagManager('subscribe', { value: (plan?.amount || 0) / 100.0 });
      return onSuccess();
    } else {
      setCurrentScreen(SCREENS.creditCard);
    }
  }

  const hasCreditCards = (creditCards[patientId] || []).length > 0;
  const activeCard = (creditCards[patientId] || []).find((card) => card.is_default);

  const subscribeNowText = 'Subscribe Now';

  const discount = plan.discount || 0;
  const subtotalThisMonth = Math.max(plan.amount - discount, 0);
  const remainingCredit = plan.amount - discount < 0 ? plan.amount - discount : 0;
  const hstThisMonth = subtotalThisMonth * HST;
  const totalThisMonth = subtotalThisMonth + hstThisMonth;

  const minimumMonths = SubscriptionUtils.getMinimumMonths(plan);
  const hasCommitment = minimumMonths > 0;

  const disclaimer =
    'Subscription will renew annually unless cancelled.' +
    (hasCommitment
      ? ` A minimum commitment of ${minimumMonths} months is required for this billing cycle.`
      : '');

  return (
    <div className="credit-card-screen page-content">
      {currentScreen === SCREENS.creditCard && (
        <>
          {loading && (
            <div className="loading-subscription">
              <ProgressMessage size={75} thickness={2} message="Please wait..." />
            </div>
          )}
          {!loading && (
            <>
              {!hasCreditCards && <CreditCardScreen patient={patient} title="Add a credit card" />}
              {hasCreditCards && (
                <div className="mb-5 column grow gap-2">
                  <span className="mb-5 block text-center">
                    This credit card will be used to process the subscription payment:
                  </span>

                  <div className="grow">
                    <CreditCardImage card={activeCard} patient={patient} preventClick={true} />
                  </div>

                  <div className="total-price">
                    {discount ? (
                      <span>
                        <b>Discount</b> ${SubscriptionUtils.convertPlanAmount(discount)}
                      </span>
                    ) : null}
                    <span>
                      <b>Subtotal</b> ${SubscriptionUtils.convertPlanAmount(subtotalThisMonth)}
                    </span>
                    <span>
                      <b>HST</b> ${SubscriptionUtils.convertPlanAmount(hstThisMonth)}
                    </span>
                    {remainingCredit > 0 && (
                      <span>
                        <b>Remaining Credit</b> $
                        {SubscriptionUtils.convertPlanAmount(remainingCredit)}
                      </span>
                    )}
                    <hr />
                    <span>
                      <b>Total per {plan.interval}</b> $
                      {SubscriptionUtils.convertPlanAmount(plan.amount * (1 + HST))}
                    </span>
                    {hasCommitment && (
                      <span>
                        <b>Total for {minimumMonths} months</b>$
                        {SubscriptionUtils.convertPlanAmount(
                          (plan.amount * minimumMonths - discount) * (1 + HST),
                        )}
                      </span>
                    )}
                    <span>
                      <b>Total this {plan.interval}</b> $
                      {SubscriptionUtils.convertPlanAmount(totalThisMonth)}
                    </span>
                  </div>

                  <div className="disclaimer">
                    <span>{disclaimer}</span>
                  </div>

                  <PromoCodeModal
                    patient={patient}
                    promoCodeModal={promoCodeModal}
                    openPromoCodeModal={setPromoCodeModal}
                  />
                  <PatientAddCardModal
                    showModal={creditCardModal}
                    onAddSuccess={() => setCreditCardModal(false)}
                    patient={patient}
                    openModal={setCreditCardModal}
                  />

                  <DefaultButton
                    name={subscribeNowText}
                    style={{ width: '100%' }}
                    className="blue"
                    onClick={subscribeWrapper}
                    loading={isSubscribing}
                  />
                  {plan.interval === 'year' && (
                    <PromoCodeButton
                      plan={plan}
                      disabled={isSubscribing}
                      onClick={() => setPromoCodeModal(true)}
                    />
                  )}
                  <span
                    className="add-card-button clickable"
                    onClick={!isSubscribing ? () => setCreditCardModal(true) : undefined}
                  >
                    + Add New Card
                  </span>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export const PromoCodeButton = ({ plan, disabled, onClick, className = 'light' }) => {
  return (
    <DefaultButton
      className={className}
      id="promo-code-button"
      name="Apply Promo or Referral Code"
      icon="tag"
      style={{ width: '100%' }}
      onClick={onClick}
      disabled={!!disabled}
    />
  );
};

Subscription.propTypes = {
  patient: PropTypes.object.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default Subscription;
